import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthUser } from '@app/account/account-api.model';
import {
  FlexRowSummary,
  ReorderItem,
} from '@app/flex-framework/flex-api.model';
import { FlexRowReorderService } from '@app/flex-framework/services/flex-row-reorder.service';
import { FlexRowService } from '@app/flex-framework/services/flex-row.service';
import { FlexViewHeaderMenuService } from '@app/flex-framework/services/flex-view-header-menu.service';
import { RecommendationsModalService } from '@app/recommendations/services/recommendations-modal.service';
import { RecommendationsService } from '@app/recommendations/services/recommendations.service';
import { ReorderModalService } from '@app/reorder-modal/reorder-modal.service';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { SimpleModalComponent } from '@app/shared/components/modal/simple-modal/simple-modal.component';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import {
  ResourcePrivacyLevel,
  TargetType,
} from '@app/shared/models/core-api.model';
import { AuthService } from '@app/shared/services/auth.service';
import { ContextService } from '@app/shared/services/context.service';
import { LDFlagsService } from '@app/shared/services/ld-flags.service';
import { ModalService } from '@app/shared/services/modal.service';
import { TargetsService } from '@app/shared/services/targets.service';
import { FollowTargetModal } from '@app/target/components/modals/follow-target-modal/follow-target-modal.component';
import { SharedTargetService } from '@app/target/services/shared-target.service';
import { TargetService } from '@app/target/services/target.service';
import {
  Target,
  TargetProperty,
  TargetSettingsFormResult,
} from '@app/target/target-api.model';
import { UserSearchModalComponent } from '@app/user/components/user-search-modal/user-search-modal.component';
import { UserSearchItem } from '@app/user/user-api.model';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, switchMap } from 'rxjs/operators';

@Component({
  selector: 'dgx-flex-view-header',
  templateUrl: './flex-view-header.component.html',
  styleUrls: ['./flex-view-header.component.scss'],
})
export class FlexViewHeaderComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  public isEditing: boolean;
  public isLeavingEditMode: boolean = false;
  public isFollowing: boolean;
  public targetType: TargetType;
  public targetMetaData: TargetProperty[] = [];
  public privacyLevelHeader: string;
  public settingsMenuConfig: MenuViewModel[];
  public i18n = this.translate.instant([
    'Target_Delete',
    'Core_Collaborator',
    'Core_DoneEditing',
    'Core_Options',
    'Core_Recommend',
    'Core_Settings',
    'Core_Follow',
    'Core_Following',
    'Core_Followers',
    'Core_MoreOptions',
    'Core_Insights',
    'Core_Clone',
    'Core_YesSure',
    'dgFlexRow_AddSection',
    'FlexCtrl_ReorderSections',
    'FlexCtrl_AddProperties',
    'FlexCtrl_EditProperties',
    'FlexCtrl_EditSettings',
    'FlexCtrl_EditPlanType',
    'TargetAuthorsForm_Authors',
    'dgFlexRow_ReorderSectionsTitle',
  ]);

  public showPlanSimplification = this.ldFlagsService.showPlanSimplification;

  @Input() public target: Target;
  @Input() public sections: FlexRowSummary[];

  private isChannelPageContext: boolean = this.contextService.urlHasContext(
    window.location.href,
    'channel'
  );
  private authUser: AuthUser;
  private context: 'channel' | 'default';

  constructor(
    private authService: AuthService,
    private contextService: ContextService,
    private flexViewHeaderMenuService: FlexViewHeaderMenuService,
    private ldFlagsService: LDFlagsService,
    private modalService: ModalService,
    private sharedTargetService: SharedTargetService,
    private targetService: TargetService,
    private targetsService: TargetsService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private recommendationsModalService: RecommendationsModalService,
    private flexRowService: FlexRowService,
    private flexRowReorderService: FlexRowReorderService,
    private reorderModalService: ReorderModalService,
    private recommendationsService: RecommendationsService
  ) {
    super();
  }

  public get canRecommend(): boolean {
    return this.recommendationsService.canRecommendResource({
      isValid:
        !this.isFeatured &&
        this.targetType !== 'Directory' &&
        this.authUser?.canRecommendItems,
      privacyLevel: this.target?.privacyId,
    });
  }

  public get isPlan(): boolean {
    return this.targetType === 'Target' || this.targetType === 'Role';
  }

  public get isFeatured(): boolean {
    return this.targetType === 'Browse';
  }

  public get canClone(): boolean {
    if (this.targetType === 'Directory') {
      return this.authService.authUser.canManageTargets;
    } else if (this.isPlan) {
      return true;
    }
    return false;
  }

  public get canEdit(): boolean {
    return this.sharedTargetService.canEditTarget(
      this.target,
      this.target?.userCanEditTarget
    );
  }

  public get canFollow(): boolean {
    return this.target?.isFollowable;
  }

  public get specificTargetTypeDisplayName(): string {
    return this.sharedTargetService.getTypeDisplayName(
      this.targetType,
      false,
      true
    );
  }

  public get targetTypeDisplayName(): string {
    return this.sharedTargetService.getTypeDisplayName(this.targetType);
  }

  public get editButtonText(): string {
    if (this.isEditing) {
      return this.translate.instant('Core_DoneEditing');
    }
    return this.translate.instant('FlexCtrl_EditPlanType', {
      planType: this.targetTypeDisplayName,
    });
  }

  public ngOnInit(): void {
    this.authUser = this.authService.authUser;
    this.route.queryParams?.subscribe((params) => {
      this.isEditing = params.editMode === 'true';
    });
    // Faux last updated date. Only in place until page refresh.
    this.flexRowService.onRowUpdated$
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.target = {
          ...this.target,
          dateModified: new Date(),
        };
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.target && changes.target.currentValue) {
      this.targetMetaData = this.target.metadata;
      this.targetType = this.target.targetType;
      this.isFollowing = this.target.isFollowing;

      if (!this.isFeatured) {
        this.privacyLevelHeader = this.getPrivacyString(
          this.target.privacyId,
          this.target.groupIds?.length
        );
      }

      this.context = this.isChannelPageContext ? 'channel' : 'default';
      this.settingsMenuConfig =
        this.flexViewHeaderMenuService.getSettingsMenuConfig(
          this.target,
          this.context
        );
    }
  }

  public toggleFollow(): any {
    if (this.isFollowing) {
      const inputs = {
        bodyText: this.translate.instant('dgTargetTile_UnfollowExplanation', {
          targetType: this.sharedTargetService.getTypeDisplayName(
            this.target.targetType
          ),
        }),
        canCancel: true,
        headerText: this.translate.instant('dgTargetTile_UnfollowConfirm', {
          targetType: this.sharedTargetService.getTypeDisplayName(
            this.target.targetType
          ),
        }),
        submitButtonText: this.i18n.Core_YesSure,
      };

      this.modalService
        .show<void>(SimpleModalComponent, { inputs })
        .pipe(
          switchMap(() => {
            return this.sharedTargetService.unfollowTarget(this.target);
          })
        )
        .subscribe(() => {
          this.isFollowing = false;
        });
    } else {
      const inputs = {
        target: this.target,
      };

      return this.modalService
        .show<boolean>(FollowTargetModal, { inputs })
        .subscribe((followPlan) => {
          if (followPlan) {
            this.isFollowing = true;
          }
        });
    }
  }

  public recommend($event) {
    const isOrgContent =
      !!this.target.organizationId &&
      Number(this.target.organizationId) ===
        this.authUser.orgInfo[0].organizationId;

    this.recommendationsModalService
      .showShareModal(
        {
          ...this.target,
          isOrgContent,
          resourceId: this.target.targetId,
        } as any,
        $event.target
      )
      .subscribe();
  }

  public editCollaborators() {
    this.modalService
      .show<UserSearchItem[]>(UserSearchModalComponent, {
        inputs: {
          users: this.target.authors,
          ariaLabel: this.i18n.Core_Collaborator,
        },
      })
      .pipe(
        switchMap((selectedCollaborators) => {
          this.target.authors = selectedCollaborators;
          const userKeys = selectedCollaborators.map((u) => u.userProfileKey);
          return this.targetsService.updateTargetAuthors(this.target, userKeys);
        })
      )
      .subscribe();
  }

  public clone() {
    this.targetService
      .showTargetCloneModal({
        target: this.target,
      })
      .subscribe((result: TargetSettingsFormResult) => {
        if (result.target) {
          // fix the typing
          this.router.navigate(['plan', (result.target as any).ReferenceId], {
            // relativeTo: this.route,
            queryParams: { editMode: false },
          });
        }
      });
  }

  public toggleEdit() {
    this.resetReorderMode();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { editMode: !this.isEditing },
      queryParamsHandling: 'merge',
    });
  }

  public editTarget() {
    this.targetService.showTargetModal({
      target: this.target,
      context: this.context,
      mode: 'edit',
    });
  }

  public reorderRows() {
    // TODO: When we have reactive stores in place, remove this if/else and associated
    // observables; it will no longer be necessary to have access to the full row object
    // to trigger updates, so we will no longer care about which tab this is triggered by
    // -- which means we can have just the *one* reorder-row method.
    // (Not being removed right now to avoid big code changes for a small bugfix:
    // PD-85603 / PD-85622)
    const url = this.router.url;
    if (url.indexOf('members') > -1 || url.indexOf('insights') > -1) {
      // we are not on the overview tab and dont have access to the current row data
      const reorderData = this.flexRowReorderService.getReorderData(
        this.sections as FlexRowSummary[]
      );

      const _onSave = (reorderedItems: ReorderItem[]) => {
        return this.flexRowReorderService
          .updateRowOrder(reorderedItems, this.sections[0].targetId)
          .subscribe(() => {
            window.location.reload();
          });
      };
      this.reorderModalService
        .openModal(
          this.i18n.dgFlexRow_ReorderSectionsTitle,
          reorderData,
          _onSave
        )
        .subscribe();
    } else {
      this.flexRowReorderService.reorderRows();
    }
  }

  /** Gets the correct privacy string for this target
   * TODO: This can be delete with the Simplified Plan LD flag
   * (method has moved to {@link flex-meta-data.component.ts } )
   * Helpful info what the concatenated strings return:
   * @return
   * "Core_PrivacyLevel0":"Private to collaborators"
   * "Core_PrivacyLevel1":"Visible to organization",
   * "Core_PrivacyLevel2":"Visible to a specific group",
   * "Core_PrivacyLevel2A":"Visible to {{groupCount}} group",
   * "Core_PrivacyLevel2B":"Visible to {{groupCount}} groups",
   * "Core_PrivacyLevel2C":"Visible to {{groupName}}",
   * "Core_PrivacyLevel3":"Visible on collaborators\u0027 profiles"
   */
  private getPrivacyString(
    privacyId: ResourcePrivacyLevel,
    groupCount: number
  ): string {
    const privacyGroupsType =
      privacyId === 2 ? (groupCount === 1 ? 'A' : 'B') : '';

    const privacyLevelResource =
      'Core_PrivacyLevel' + privacyId + privacyGroupsType;

    return this.translate.instant(privacyLevelResource, {
      groupCount: groupCount,
    });
  }

  private resetReorderMode(): void {
    let isReorderModeActive;
    this.flexRowReorderService.isReorderModeActive$
      .pipe(this.takeUntilDestroyed())
      .subscribe((isReorderMode) => (isReorderModeActive = isReorderMode));
    if (isReorderModeActive) {
      this.flexRowReorderService.toggleReorderMode(false);
    }
  }
}
